/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 * This JavaScript dictionary object was provided under a free license from a answer to a
 * stackoverflow.com question and is used here without restrictions.
 */

import EventEmitter from '../support/eventemitter';

/**
 * @callback DictionaryForEachCallback
 * @param {*} value - The item in the dictionary.
 * @param {string|number|EntityLinker} key - The key for the corresponding item in the dictionary passed to the callback
 * @param {string|null} uid - The uid, if provided or collected, for this item.
 */

/**
 * @callback DictionarySomeCallback
 * @param {*} value - The item in the dictionary passed to the callback.
 * @param {string|number|EntityLinker} key - The key for the corresponding item in the dictionary passed to the callback
 * @param {string|null} [uid = null] - The UID of the corresponding item in the dictionary (if specified)
 */

/**
 * @callback DictionaryFilterCallback
 * @param {*} value - The item in the dictionary passed to the callback.
 * @param {string|number|EntityLinker} key - The key for the corresponding item in the dictionary passed to the callback
 * @param {string|null} [uid = null] - The UID of the corresponding item in the dictionary (if specified)
 */

/**
 * This JavaScript dictionary object was provided under a free license from a answer to a
 * stackoverflow.com question and is used here without restrictions.
 * http://stackoverflow.com/questions/3559070/are-there-dictionaries-in-javascript-like-python
 * @constructor
 */
export class Dictionary extends EventEmitter {
    /**
     * Create an object that marshals key, value pairs (with additional UID key).
     * Dictionary can also be read-only if desired.
     * @param {Array|null} [keys = []] - (OPTIONAL) Default array for keys
     * @param {Array|null} [values = []] - (OPTIONAL) Default array for values
     * @param {Array|null} [uidKeys = []] - (OPTIONAL) Default array for UID keys
     * @param {boolean} [readOnly = false] - (OPTIONAL) TRUE, dictionary is read-only; FALSE (DEFAULT), dictionary is read-write
     */
    constructor(keys = [], values = [], uidKeys = [], readOnly = false) {
        super();

        /**
         * Array for keys
         * @type {Array}
         */
        this.Keys = (Array.isArray(keys) && keys.slice()) || [];

        /**
         * Array for UID keys
         * @type {Array}
         */
        this.KeysUID = (Array.isArray(uidKeys) && uidKeys.slice()) || [];

        /**
         * Array for values
         * @type {Array}
         */
        this.Values = (Array.isArray(values) && values.slice()) || [];

        /**
         * Internal flag to indicate if dictionary is read-only
         * @type {boolean}
         * @protected
         */
        this._readOnly = readOnly;
    }

    /**
     * Returns corresponding value of a key or UID of passed entity linker object from the Dictionary.
     * @param {number|string|EntityLinker|Object} key
     * @returns {*}
     */
    getVal (key) {
        if (key == null) {
            return null;
        }

        // determine the type of key provided and setup to search the correct key array.
        let searchArray, lookupVal;
        if (typeof key === "number" || typeof key === "string") {
            lookupVal = key;
            searchArray = this.Keys;
        }
        else if (typeof key === "object" && key.UID != null) {
            lookupVal = key.UID.toString().toLowerCase();
            searchArray = this.KeysUID;
        }
        else {
            return null;
        }

        // does this already exist?
        let index = searchArray.indexOf(lookupVal);
        if (index > -1) {
            // yes - return it.
            return this.Values[index];
        }
        // default to null if not found.
        return null;
    }

    /**
     * Return a boolean value to specify if the key value was found in the Dictionary.
     * NOTE: this will also return if the UID value or UID from a passed EntityLinker object was found.
     * @param {string|number|EntityLinker} key
     * @returns {boolean} - TRUE, specified key value was found; FALSE, the specified key value was not found.
     */
    containsKey (key) {
        if (key == null) {
            return false;
        }

        // determine the type of key provided and setup to search the correct key array.
        let searchArray, lookupVal;
        if (typeof (key) === "number" || typeof (key) === "string") {
            lookupVal = key;
            searchArray = this.Keys;
        }
        else if (typeof (key) === "object" && key.UID != null) {
            lookupVal = key.UID;
            searchArray = this.KeysUID;
        }
        else {
            return false;
        }

        // return if the key was found in the appropriate array.
        return (searchArray.indexOf(lookupVal) > -1);
    }

    /**
     * Adds a unique key value pair
     * @param {number|string|EntityLinker} key - a unique identifier for the value; if an EntityLinker, will use the EntityLinker!
     * @param {*} val - A value or object to store under the associated key
     * @param {string|null} [uid = null] - (OPTIONAL) UID for the value; if value has a UID property, it will be used.
     * @returns {*} - The value that was added
     */
    add (key, val, uid = null) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (key == null) {
            return "Key cannot be null";
        }

        // determine the type of key provided and setup to search the correct key array.
        let searchArray, lookupVal, haveKey = false;
        if (typeof (key) === "number" || typeof (key) === "string") {
            lookupVal = key;
            haveKey = true;
            searchArray = this.Keys;
        }
        else if (typeof (key) === "object" && key.UID != null) {
            lookupVal = key.UID.toString().toLowerCase();
            searchArray = this.KeysUID;
        }
        else {
            return null;
        }

        let pushedUID = null;

        if (uid != null) {
            // use parameter if specified.
            pushedUID = uid.toString().toLowerCase();
        }
        else if (typeof (val) === "object" && val.UID != null) {
            // use val.UID property if possible
            pushedUID = val.UID.toString().toLowerCase();
        }

        // does this already exist?
        let index = searchArray.indexOf(lookupVal);
        if (index > -1) {
            //Replace the key with the value given...EK
            this.Values[index] = val;

            //We emit an event that a new item was updated to the dictionary...EK
            this.emit('add', {key: key, UID: pushedUID, value: val});
            return val;
        }

        // Allow only strings or numbers as keys
        if (haveKey) {
            this.Keys.push(key);
            this.Values.push(val);

            this.KeysUID.push(pushedUID);

            //We emit an event that a new item was added to the dictionary...EK
            this.emit('add', {key: key, UID: pushedUID, value: val});
        }
        else {
            return "Only number or string can be key!";
        }
    }

    /**
     * Removes a key value pair
     * @param {string|number|EntityLinker} key - The key value to use to search for the item to remove
     * @returns {*} - The value that was removed, if any was found
     */
    remove (key) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (key == null) {
            return "Key cannot be null";
        }

        // determine the type of key provided and setup to search the correct key array.
        let searchArray, lookupVal;
        if (typeof (key) === "number" || typeof (key) === "string") {
            lookupVal = key;
            searchArray = this.Keys;
        }
        else if (typeof (key) === "object" && key.UID != null) {
            lookupVal = key.UID.toString().toLowerCase();
            searchArray = this.KeysUID;
        }
        else {
            return null;
        }

        // does this already exist?
        let index = searchArray.indexOf(lookupVal);

        if (index > -1) {
            this.Keys.removeAt(index);
            this.KeysUID.removeAt(index);
            this.emit('remove', {key: key, value: this.Values[index]});
            return this.Values.splice(index, 1);
        }
        return null;
    }

    /**
     * Clears all internal arrays.
     */
    clear () {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        let removedKeys = this.Keys.slice();
        this.Keys.length = 0;
        this.KeysUID.length = 0;
        this.Values.length = 0;
        this.emit('clear', removedKeys);
    }

    /**
     * Gets a count on the dictionary.
     * @returns {number} - The number of value items in the dictionary
     */
    count () {
        return this.Keys.length;
    }

    /**
     * Insert logic that will keep both key / values in step.
     * @param {number|string|EntityLinker} key - a unique identifier for the value; if an EntityLinker, will use the EntityLinker!
     * @param {*} val - A value or object to store under the associated key
     * @param {string|null} [uid = null] - (OPTIONAL) UID for the value; if value has a UID property, it will be used.
     * @param {number} [atIndex = -1] - The index to insert at; if beyond the size of the dictionary, will be inserted at the end
     * @returns {*} - The value that was inserted
     */
    insert (key, val, uid = null, atIndex = -1) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (atIndex === -1) {
            this.Keys.push(key);
            this.Values.push(val);
        }
        else {
            this.Keys.splice(atIndex, 0, key);
            this.Values.splice(atIndex, 0, val);
        }

        if (uid != null) {
            // we were given a uid value, use it.
            if (atIndex === -1) {
                this.KeysUID.push(uid.toString().toLowerCase());
            }
            else {
                this.KeysUID.splice(atIndex, 0, uid.toString().toLowerCase());
            }
        }
        else if (typeof (val) === "object" && val.UID != null) {
            // try to use the UID property of the val parameter
            if (atIndex === -1) {
                this.KeysUID.push(val.UID.toString().toLowerCase());
            }
            else {
                this.KeysUID.splice(atIndex, 0, val.UID.toString().toLowerCase());
            }
        }
        else {
            // no uid; insert NULL value.
            if (atIndex === -1) {
                this.KeysUID.push(null);
            }
            else {
                this.KeysUID.splice(atIndex, 0, null);
            }
        }

        this.emit('insert', { key: key, value: val }, uid, atIndex);
        return val;
    }

    /**
     * Get the actual index (not the key) for the array (used for inserting at)
     * @param {*} val - A value or object to store under the associated key
     * @returns {number} - The index that the value is at; -1 if not found in the dictionary
     */
    indexOf (val) {
        return this.Values.indexOf(val);
    }

    /**
     * Return the index of the specified key value.
     * @param {number|string|EntityLinker} key - a unique identifier for the value
     * @returns {number} - The index that the key is at; -1 if not found in the dictionary
     */
    keyIndexOf (key) {
        return this.Keys.indexOf(key);
    }

    /**
     * Return the index of the specified string UID.
     * @param {string} uid - UID for the value
     * @returns {number} - The index that the UID is at; -1 if not found in the dictionary
     */
    uidIndexOf (uid) {
        return this.KeysUID.indexOf(uid);
    }

    /**
     * Allow swapping values in the dictionary and keep the keys in sync.
     * @param {number|string|EntityLinker} firstIndex - The index of the value or the Key value to swap.
     * @param {number|string|EntityLinker} secondIndex - The index of the value or the Key value to swap places with.
     * @param {boolean} [forceKeys = false] - TRUE, force using index parameters as Key values to lookup position; FALSE (DEFAULT), only use index parameters as Key values if they are strings
     */
    swap (firstIndex, secondIndex, forceKeys = false) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        // do the swap directly rather than counting on a Array.swap() method.
        if (forceKeys || typeof firstIndex === 'string') {
            // assume that the "firstIndex" is a Key value and lookup its position
            firstIndex = this.Keys.indexOf(firstIndex);
        }
        else if (typeof firstIndex === 'object' && firstIndex.UID != null) {
            firstIndex = this.KeysUID.indexOf(firstIndex.UID.toLowerCase());
        }

        if (forceKeys || typeof secondIndex === 'string') {
            // assume that the "secondIndex" is a Key value and lookup its position
            secondIndex = this.Keys.indexOf(secondIndex);
        }
        else if (typeof secondIndex === 'object' && secondIndex.UID != null) {
            secondIndex = this.KeysUID.indexOf(secondIndex.UID.toLowerCase());
        }

        let v = this.Values[firstIndex];
        this.Values[firstIndex] = this.Values[secondIndex];
        this.Values[secondIndex] = v;

        let k = this.Keys[firstIndex];
        this.Keys[firstIndex] = this.Keys[secondIndex];
        this.Keys[secondIndex] = k;

        let u = this.KeysUID[firstIndex];
        this.KeysUID[firstIndex] = this.KeysUID[secondIndex];
        this.KeysUID[secondIndex] = u;

        this.emit("swap", {key: k, value: v}, firstIndex, {key: this.Keys[firstIndex], value: this.Values[firstIndex]}, secondIndex);
    }

    /**
     * Return a NEW dictionary that combines this dictionary with the specified dictionary (added after the originals).
     * NOTE: This will *NOT* modify the existing dictionary as append() does. Because of this, it does not matter if
     * the original dictionary is read only; but it will inherit the read only status of the original dictionary.
     * @param {Dictionary|null} [secDictionary = null]
     * @returns {Dictionary}
     */
    concat (secDictionary) {
        let d = new Dictionary(this.Keys, this.Values, this.KeysUID);

        if (secDictionary != null) {
            d.Keys = this.Keys.concat(secDictionary.Keys);
            d.KeysUID = this.KeysUID.concat(secDictionary.KeysUID);
            d.Values = this.Values.concat(secDictionary.Values);
        }

        return d;
    }

    /**
     * Update THIS dictionary by adding the keys and values of the specified dictionary (at the end).
     * NOTE: This *WILL NOT* check for and remove duplicate values with identical keys!!
     * This is available as it is faster than merge() but should used where it is understood you may create duplicate keys.
     * @param {Dictionary} [appendDictionary = null] - The dictionary to append
     * @returns {Dictionary}
     */
    append (appendDictionary) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (appendDictionary != null) {
            this.Keys = this.Keys.concat(appendDictionary.Keys);
            this.Values = this.Values.concat(appendDictionary.Values);
            this.KeysUID = this.KeysUID.concat(appendDictionary.KeysUID);
        }

        this.emit('append', { appended: appendDictionary });

        return this;
    }

    /**
     * Create a new Dictionary object that REFERENCES TO THE SAME ARRAYS as this one.
     * This can be used to create a read-only reference to a writable Dictionary, or vice versa.
     * @param {boolean} [readOnly = false] - (OPTIONAL) TRUE, dictionary is read-only; FALSE (DEFAULT), dictionary is read-write
     */
    clone (readOnly = false) {
        let d = new Dictionary([], [], [], readOnly);
        d.Keys = this.Keys;
        d.Values = this.Values;
        d.KeysUID = this.KeysUID;
        return d;
    }

    /**
     * Create a new Dictionary object that contains DIFFERENT ARRAYS with the same items, by reference.
     * @param {boolean} [readOnly = false] - (OPTIONAL) TRUE, dictionary is read-only; FALSE (DEFAULT), dictionary is read-write
     * @returns {Dictionary}
     */
    referenceClone (readOnly = false) {
        return new Dictionary(this.Keys, this.Values, this.KeysUID, readOnly);
    }

    /**
     * Call the specified function for each item in the dictionary.
     * The callback will be called with the Key, Item, and UID key as parameters for each item.
     * Any modifications to the dictionary during the operation will not be reflected during the operation;
     * all items that exist when it starts will be visited in the order they were at operation start.
     * @param {DictionaryForEachCallback} callback - the function to call for each item in the dictionary.
     * @param {boolean} [RunAsAsync = false] - TRUE, await the callback; FALSE (DEFAULT), do not await the callback
     * @returns {Dictionary|Promise<*>} - If RunAsAsync is true; will return the promise
     */
    forEach (callback, RunAsAsync = false) {
        if (callback == null || typeof callback !== 'function') {
            return null;
        }

        let keys = this.Keys,
            values = this.Values,
            keysUID = this.KeysUID;

        let asyncLoop = async () => {
            for (let [index, val] of values.entries()) {
                await callback(val, keys[index], keysUID[index]);
            }

            return this;
        };

        if (RunAsAsync) {
            return asyncLoop();
        }
        else {
            values.forEach((val, index) => {
                callback(val, keys[index], keysUID[index]);
            });
        }

        return this;
    }

    /**
     * Implement the Array "some" functionality on the dictionary; only the "Values" will be passed to the test function.
     * Any modifications to the dictionary during the operation will not be reflected during the operation;
     * all items that exist when it starts will be visited in the order they were at operation start.
     * @param {DictionarySomeCallback} callback - The function to use for checking the condition for each item in the dictionary values.
     * @returns {boolean}
     */
    some (callback) {
        if (callback == null || typeof callback !== 'function') {
            return false;
        }

        let keys = this.Keys,
            values = this.Values,
            keysUID = this.KeysUID;

        return !!values.some((val, index) => {
            return callback(val, keys[index], keysUID[index]);
        });
    }

    /**
     * Merge the items and keys of the specified merge source into this dictionary. The merge source will be unmodified.
     * NOTE: This *WILL* overwrite any values in the dictionary that has a matching key value from the mergeSource!!
     * @param {Dictionary} mergeSource - The dictionary to merge with this one. The merge source will be unmodified.
     */
    merge (mergeSource) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (!(mergeSource instanceof Dictionary)) {
            throw new Error('Specified merge source is not a Dictionary.');
        }

        mergeSource.forEach((item, key, uid) => {
            this.add(key, item, uid);
        });
    }

    /**
     * Replace the original key parameter with the value of the new key parameter.
     * This will mean that the original key will no longer return any items.
     * @param {number|string|EntityLinker} originalKey - a unique identifier for the value to find
     * @param {number|string|EntityLinker} newKey - a unique identifier to replace the original with
     * @returns {boolean} - TRUE, successfully updated key; FALSE, could not find original key, or could not update it.
     */
    updateKey (originalKey, newKey) {
        if (this._readOnly) {
            throw new Error('Dictionary is read-only.');
        }

        if (newKey == null) {
            // a key cannot be null; cannot allow this change.
            return false;
        }

        let idx = this.Keys.indexOf(originalKey);

        if (idx > -1) {
            // found the original key; update that index with the new key value.
            this.Keys[idx] = newKey;
            this.emit('updateKey', originalKey, newKey);
            return true;
        }

        return false;
    }

    /**
     * Implement the Array "filter" functionality on the dictionary.
     * @param {DictionaryFilterCallback} callback - Function to filter the dictionary items
     * @returns {Dictionary}
     */
    filter (callback) {
        let keys = this.Keys,
            values = this.Values,
            keysUID = this.KeysUID,
            newKeys = [],
            newValues = [],
            newKeysUID = [];

        // loop through the values array of the dictionary to filter
        values.forEach((value, index) => {
            let key = keys[index],
                keyUID = keysUID[index];

            // if the callback is TRUE, then add these entries to the new dictionary.
            if (callback(value, key, keyUID)) {
                newKeys.push(key);
                newValues.push(value);
                newKeysUID.push(keyUID);
            }
        });

        // return the resultant items in a new dictionary
        return new Dictionary(newKeys, newValues, newKeysUID);
    }
}
